import { toBitmap, type Sprite } from '../spriteLayout';
import type { Frame } from './types';
import { bustCache } from './battleAssetVersion';
import { getAssetImage } from '../../../lib/assetStore';

// --- run-sheet slicing (shared logic with the editor's sprites.ts sliceRun) ---
function boxOf(c: HTMLCanvasElement) {
  const x = c.getContext('2d')!;
  let bx = c.width, by = c.height, mx = 0, my = 0, found = false;
  try {
    const d = x.getImageData(0, 0, c.width, c.height).data;
    for (let k = 0; k < d.length; k += 4) {
      if (d[k + 3] > 24) {
        const p = k / 4, px = p % c.width, py = (p / c.width) | 0;
        if (px < bx) bx = px; if (px > mx) mx = px; if (py < by) by = py; if (py > my) my = py;
        found = true;
      }
    }
  } catch { /* tainted — same-origin only */ }
  return { bx, by, mx, my, found };
}
function cropTight(src: HTMLCanvasElement, x0: number, x1: number): Sprite {
  const w = Math.max(1, x1 - x0 + 1);
  const cell = document.createElement('canvas');
  cell.width = w; cell.height = src.height;
  cell.getContext('2d')!.drawImage(src, -x0, 0);
  const b = boxOf(cell);
  const bx = b.found ? b.bx : 0, by = b.found ? b.by : 0;
  const bw = b.found ? b.mx - b.bx : w, bh = b.found ? b.my - b.by : src.height;
  const out = document.createElement('canvas');
  out.width = Math.max(1, bw); out.height = Math.max(1, bh);
  out.getContext('2d')!.drawImage(cell, -bx, -by);
  return { canvas: out, bx: 0, by: 0, bw, bh };
}

/** Slice a horizontal run spritesheet into `n` per-frame sprites, cutting on the
 * transparent column gaps (codex doesn't pack even cells) and registering every
 * frame onto one common canvas so the animation doesn't grow/shrink between poses.
 * Mirror of the editor's sprites.ts sliceRun so the game plays the run identically. */
export function sliceRunSheet(sheet: Sprite, n: number): Promise<Sprite[]> {
  // the sheet arrives as a bitmap/image (loadKeyedSprite retains no canvases — iOS purges
  // canvas buffers); rasterize it once for the pixel scans, freed at the end
  const W = sheet.canvas.width, H = sheet.canvas.height;
  const src = document.createElement('canvas');
  src.width = W; src.height = H;
  src.getContext('2d', { willReadFrequently: true })?.drawImage(sheet.canvas, 0, 0);
  let occ: Uint8Array | null = null;
  try {
    const d = src.getContext('2d')!.getImageData(0, 0, W, H).data;
    occ = new Uint8Array(W);
    for (let y = 0; y < H; y++) { const row = y * W; for (let x = 0; x < W; x++) if (d[(row + x) * 4 + 3] > 24) occ[x] = 1; }
  } catch { occ = null; }
  const raw: Array<[number, number]> = [];
  if (occ) {
    let s = -1;
    for (let x = 0; x <= W; x++) {
      const on = x < W && occ[x] === 1;
      if (on && s < 0) s = x;
      if (!on && s >= 0) { raw.push([s, x - 1]); s = -1; }
    }
  }
  const GAP = Math.max(4, W * 0.012), MINW = Math.max(8, W * 0.02);
  const merged: Array<[number, number]> = [];
  for (const [a, b] of raw) {
    const last = merged[merged.length - 1];
    if (last && a - last[1] - 1 < GAP) last[1] = b; else merged.push([a, b]);
  }
  const figs = merged.filter(([a, b]) => b - a + 1 >= MINW);
  const cols: Array<[number, number]> = figs.length >= 2
    ? figs
    : Array.from({ length: n }, (_, i): [number, number] => [Math.floor((i * W) / n), Math.floor(((i + 1) * W) / n) - 1]);
  const crops = cols.map(([x0, x1]) => cropTight(src, x0, x1));
  src.width = 0;
  src.height = 0;
  const maxW = Math.max(1, ...crops.map((c) => c.bw));
  const maxH = Math.max(1, ...crops.map((c) => c.bh));
  const m = Math.max(8, Math.round(maxH * 0.06));
  const cw = maxW + 2 * m, ch = maxH + 2 * m;
  // frames become ImageBitmaps like every other sprite (per-frame texture blits, purge-safe);
  // the registration canvas is transient, freed as soon as the bitmap exists
  return Promise.all(crops.map(async (cr) => {
    const c = document.createElement('canvas');
    c.width = cw; c.height = ch;
    const dx = Math.round((cw - cr.bw) / 2);
    c.getContext('2d')!.drawImage(cr.canvas, dx, m);
    const bmp = await toBitmap(c);
    if (bmp !== c) { c.width = 0; c.height = 0; }
    return { canvas: bmp, bx: m, by: m, bw: maxW, bh: maxH };
  }));
}

// getContext/getImageData can throw on a memory-starved Android WebView — an async throw
// surfaces the loading error screen instead of an uncaught, tree-killing throw.
export async function loadFrames(src: string): Promise<{ img: ImageBitmap | HTMLImageElement; frames: Frame[] }> {
  const img = await getAssetImage(bustCache(src));
  const c = document.createElement('canvas');
  c.width = img.width; c.height = img.height;
  const cx = c.getContext('2d', { willReadFrequently: true });
  if (!cx) throw new Error(`no 2d context for ${src}`);
  cx.drawImage(img, 0, 0);
  const { data } = cx.getImageData(0, 0, c.width, c.height);
  const colFilled = (x: number) => {
    for (let y = 0; y < c.height; y++) if (data[(y * c.width + x) * 4 + 3] > 40) return true;
    return false;
  };
  const bands: Array<[number, number]> = [];
  let start = -1;
  const GAP = Math.max(4, (c.width * 0.012) | 0);
  let emptyRun = 0;
  for (let x = 0; x < c.width; x++) {
    if (colFilled(x)) { if (start < 0) start = x; emptyRun = 0; }
    else if (start >= 0) {
      emptyRun++;
      if (emptyRun >= GAP) { bands.push([start, x - emptyRun]); start = -1; emptyRun = 0; }
    }
  }
  if (start >= 0) bands.push([start, c.width - 1]);
  const frames: Frame[] = bands.map(([x0, x1]) => {
    let y0 = c.height, y1 = 0;
    for (let x = x0; x <= x1; x++) {
      for (let y = 0; y < c.height; y++) {
        if (data[(y * c.width + x) * 4 + 3] > 40) { if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
    }
    return { sx: x0, sw: x1 - x0 + 1, sy: y0, sh: y1 - y0 + 1 };
  });
  const bmp = await toBitmap(img);
  return { img: bmp, frames };
}

/** Store-backed plain image (IDB-cached blob → decoded HTMLImageElement). */
export function loadPlain(src: string): Promise<HTMLImageElement> {
  return getAssetImage(bustCache(src));
}
